import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";

export interface Toast {
	id: string;
	title: string;
	description?: string;
	variant?: "default" | "destructive" | "success" | "warning";
	duration?: number;
}

export interface ToastOptions {
	title: string;
	description?: string;
	variant?: "default" | "destructive" | "success" | "warning";
	duration?: number;
}

interface ToastContextType {
	toasts: Toast[];
	exitingToasts: Toast[];
	toast: (options: ToastOptions) => string;
	dismiss: (id: string) => void;
	dismissAll: () => void;
}

const EXIT_ANIMATION_MS = 300;

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const [toasts, setToasts] = useState<Toast[]>([]);
	const [exitingToasts, setExitingToasts] = useState<Toast[]>([]);

	const removeExiting = useCallback((id: string) => {
		setExitingToasts(prev => prev.filter(t => t.id !== id));
	}, []);

	const toast = useCallback(
		(options: ToastOptions) => {
			const id = Math.random().toString(36).substr(2, 9);
			const newToast: Toast = {
				id,
				title: options.title,
				description: options.description,
				variant: options.variant || "success",
				duration: options.duration || 8000,
			};

			setToasts(prev => [...prev, newToast]);

			// Auto remove: move to exiting first so exit animation runs, then remove
			setTimeout(() => {
				setToasts(prev => {
					const t = prev.find(x => x.id === id);
					if (t) setExitingToasts(e => [...e, t]);
					return prev.filter(t => t.id !== id);
				});
				setTimeout(() => removeExiting(id), EXIT_ANIMATION_MS);
			}, newToast.duration);

			return id;
		},
		[removeExiting],
	);

	const dismiss = useCallback(
		(id: string) => {
			setToasts(prev => {
				const t = prev.find(x => x.id === id);
				if (t) {
					setExitingToasts(e => [...e, t]);
					setTimeout(() => removeExiting(id), EXIT_ANIMATION_MS);
				}
				return prev.filter(t => t.id !== id);
			});
		},
		[removeExiting],
	);

	const dismissAll = useCallback(() => {
		setToasts(prev => {
			setExitingToasts(e => [...e, ...prev]);
			setTimeout(() => setExitingToasts([]), EXIT_ANIMATION_MS);
			return [];
		});
	}, []);

	return (
		<ToastContext.Provider value={{ toasts, exitingToasts, toast, dismiss, dismissAll }}>
			{children}
		</ToastContext.Provider>
	);
};

export const useToast = () => {
	const context = useContext(ToastContext);
	if (context === undefined) {
		throw new Error("useToast must be used within a ToastProvider");
	}
	return context;
};
